/**
 * VestingSummary.js - Summary Cards Component
 * Displays Total Allocated, Unlocked & Claimable, Locked & Vesting
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Coins, 
  Unlock, 
  Lock, 
  TrendingUp,
  Clock,
  Calendar,
  Percent
} from 'lucide-react';

import { formatTokenAmount, animateValue } from '../utils/vestingHelpers';

import './VestingSummary.css';

const VestingSummary = ({ data, state, blockchainData }) => {
  const [animatedValues, setAnimatedValues] = useState({
    totalAllocated: 0,
    unlocked: 0,
    locked: 0
  });

  const hasAnimated = useRef(false);

  // Get values based on state
  const getValues = () => {
    if (state === 'pre-tge') {
      return {
        totalAllocated: data?.totalAllocated || 0,
        unlocked: 0,
        locked: data?.totalAllocated || 0,
        unlockedLabel: 'Est. Instant at TGE',
        lockedLabel: 'Est. Vested Amount',
        estimatedInstant: (data?.totalAllocated || 0) * 0.5,
        estimatedVested: (data?.totalAllocated || 0) * 0.5
      };
    }
    
    if (state === 'complete') {
      return {
        totalAllocated: data?.totalAllocated || 0,
        unlocked: data?.totalAllocated || 0,
        locked: 0,
        unlockedLabel: 'Total Unlocked',
        lockedLabel: 'Remaining Locked'
      };
    }
    
    // During vesting
    return {
      totalAllocated: data?.totalAllocated || 0,
      unlocked: data?.unlockedClaimable || Math.round((data?.totalAllocated || 0) * 0.5),
      locked: data?.lockedVesting || Math.round((data?.totalAllocated || 0) * 0.5),
      unlockedLabel: 'Unlocked & Claimable',
      lockedLabel: 'Locked & Vesting'
    };
  };

  const values = getValues();

  // Animate values on mount
  useEffect(() => {
    if (hasAnimated.current || !data) return;
    hasAnimated.current = true;

    // Animate total allocated
    animateValue(0, values.totalAllocated, 1500, (val) => {
      setAnimatedValues(prev => ({ ...prev, totalAllocated: Math.round(val) }));
    });

    // Animate unlocked (with delay)
    setTimeout(() => {
      animateValue(0, values.unlocked, 1200, (val) => {
        setAnimatedValues(prev => ({ ...prev, unlocked: Math.round(val) }));
      });
    }, 200);

    // Animate locked (with delay)
    setTimeout(() => {
      animateValue(0, values.locked, 1200, (val) => {
        setAnimatedValues(prev => ({ ...prev, locked: Math.round(val) }));
      });
    }, 400);
  }, [data, values.totalAllocated, values.unlocked, values.locked]);

  // Calculate vesting progress percentage
  const getVestingProgress = () => {
    if (state === 'pre-tge') return 0;
    if (state === 'complete') return 100;
    
    const progress = data?.vestingStatus?.percentComplete || 0;
    return Math.min(100, Math.max(0, progress));
  };

  // Get remaining time text
  const getRemainingTime = () => {
    if (state === 'pre-tge') return 'Awaiting TGE';
    if (state === 'complete') return 'Complete';
    
    const remaining = data?.vestingStatus?.remainingDays || 0;
    if (remaining > 30) {
      return `${Math.round(remaining / 30)} months remaining`;
    }
    return `${remaining} days remaining`;
  };

  return (
    <div className="vesting-summary">
      {/* Card 1: Total Allocated */}
      <div className="summary-card total-allocated">
        <div className="card-icon">
          <Coins size={28} />
        </div>
        <div className="card-content">
          <span className="card-label">Total Allocated</span>
          <span className="card-value">
            {animatedValues.totalAllocated.toLocaleString()}
            <span className="token-symbol">TPHILE</span>
          </span>
          <div className="card-meta">
            <TrendingUp size={14} />
            <span>All-time rewards</span>
          </div>
        </div>
        <div className="card-decoration" />
      </div>

      {/* Card 2: Unlocked & Claimable */}
      <div className={`summary-card unlocked ${state === 'complete' ? 'complete' : ''}`}>
        <div className="card-icon">
          <Unlock size={28} />
        </div>
        <div className="card-content">
          <span className="card-label">
            {values.unlockedLabel}
          </span>
          <span className="card-value">
            {state === 'pre-tge' 
              ? `~${Math.round(values.estimatedInstant).toLocaleString()}`
              : animatedValues.unlocked.toLocaleString()
            }
            <span className="token-symbol">TPHILE</span>
          </span>
          <div className="card-meta">
            {state === 'pre-tge' ? (
              <>
                <Percent size={14} />
                <span>50% of total at TGE</span>
              </>
            ) : state === 'complete' ? (
              <>
                <Calendar size={14} />
                <span>Fully unlocked</span>
              </>
            ) : (
              <>
                <Percent size={14} />
                <span>{getVestingProgress().toFixed(1)}% of vested</span>
              </>
            )}
          </div>
        </div>
        {state === 'complete' && <div className="complete-badge">✓</div>}
        <div className="card-decoration unlocked-dec" />
      </div>

      {/* Card 3: Locked & Vesting */}
      <div className={`summary-card locked ${state === 'complete' ? 'complete' : ''}`}>
        <div className="card-icon">
          <Lock size={28} />
        </div>
        <div className="card-content">
          <span className="card-label">
            {values.lockedLabel}
          </span>
          <span className="card-value">
            {state === 'pre-tge'
              ? `~${Math.round(values.estimatedVested).toLocaleString()}`
              : animatedValues.locked.toLocaleString()
            }
            <span className="token-symbol">TPHILE</span>
          </span>
          <div className="card-meta">
            <Clock size={14} />
            <span>{getRemainingTime()}</span>
          </div>
        </div>

        {/* Progress bar for vesting */}
        {state === 'vesting' && (
          <div className="vesting-progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${100 - getVestingProgress()}%` }}
            />
          </div>
        )}
        <div className="card-decoration locked-dec" />
      </div>
    </div>
  );
};

export default VestingSummary;

